import type * as d3 from 'd3'
import type { MutableRefObject } from 'react'

import type { ChartScales } from '@/features/bidding/types/bidLadder'

type LegendItem = {
  name: string
  type: 'range' | 'line'
  color: string
  visible: boolean
  price?: number
}

export function renderLegend(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  scales: ChartScales,
  legendItems: LegendItem[],
  visibilityStateRef: MutableRefObject<Map<string, boolean>>,
  onToggle: () => void,
  position: 'top' | 'bottom' = 'top',
) {
  const { chartWidth, chartHeight } = scales
  const legendGroup = g.append('g').attr('class', position === 'top' ? 'top-legend' : 'bottom-legend')

  // Position legend based on top or bottom
  const baseY = position === 'top' ? -30 : chartHeight + 60
  const lineLength = 30
  const gap = 8
  const itemGap = 20
  const rowHeight = 24 // Height between wrapped rows
  const minLeftMargin = 10 // Minimum space from left edge before wrapping

  // Both top and bottom legends start from right and go right-to-left
  let currentX = chartWidth
  let currentRow = 0

  const itemsToRender = legendItems.slice().reverse()

  for (const item of itemsToRender) {
    // Calculate item width first to check if we need to wrap
    const estimatedItemWidth = lineLength + gap + item.name.length * 7 + itemGap // Rough estimate

    // Check if item would overflow left edge - wrap to next row
    if (currentX - estimatedItemWidth < minLeftMargin && currentX < chartWidth) {
      currentRow++
      currentX = chartWidth
    }

    // Calculate Y position based on current row
    // For top legend: rows go upward (negative direction)
    // For bottom legend: rows go downward (positive direction)
    const legendY = position === 'top' ? baseY - currentRow * rowHeight : baseY + currentRow * rowHeight

    const itemGroup = legendGroup
      .append('g')
      .attr('class', 'legend-item')
      .style('cursor', 'pointer')
      .style('pointer-events', 'all')

    const textAnchor = 'end' // Both legends use right-to-left layout

    const text = itemGroup
      .append('text')
      .attr('x', currentX)
      .attr('y', legendY + 4)
      .attr('font-size', '11px')
      .attr('text-anchor', textAnchor)
      .attr('font-weight', 400)
      .attr('fill', '#374151')
      .attr('opacity', item.visible ? 1 : 0.35)
      .style('letter-spacing', '0.01em')
      .text(`${item.name}`)

    const textWidth = (text.node() as SVGTextElement).getBBox().width

    // Calculate positions for right-to-left layout (same for both top and bottom)
    const lineX1 = currentX - textWidth - lineLength - gap
    const lineX2 = currentX - textWidth - gap
    const rectX = currentX - textWidth - lineLength - gap
    const clickRectX = currentX - textWidth - lineLength - gap - 4

    if (item.type === 'line') {
      itemGroup
        .append('line')
        .attr('x1', lineX1)
        .attr('x2', lineX2)
        .attr('y1', legendY)
        .attr('y2', legendY)
        .attr('stroke', item.color)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '4,4')
        .attr('opacity', item.visible ? 0.85 : 0.25)
    } else if (item.type === 'range') {
      itemGroup
        .append('rect')
        .attr('x', rectX)
        .attr('y', legendY - 5)
        .attr('width', lineLength)
        .attr('height', 10)
        .attr('fill', item.color)
        .attr('opacity', item.visible ? 0.3 : 0.1)
        .attr('rx', 2)
    }

    const clickRect = itemGroup
      .insert('rect', ':first-child')
      .attr('x', clickRectX)
      .attr('y', legendY - 10)
      .attr('width', textWidth + lineLength + gap + 8)
      .attr('height', 20)
      .attr('fill', 'transparent')
      .attr('rx', 3)

    itemGroup
      .on('mouseenter', function () {
        if (item.visible) {
          clickRect.attr('fill', '#f9fafb')
        }
      })
      .on('mouseleave', function () {
        clickRect.attr('fill', 'transparent')
      })
      .on('click', function (event) {
        event.stopPropagation()

        const currentVisibility = visibilityStateRef.current.get(item.name) ?? true
        visibilityStateRef.current.set(item.name, !currentVisibility)

        onToggle()
      })

    // Update currentX for next item (right-to-left for both top and bottom)
    currentX = currentX - textWidth - lineLength - gap - itemGap
  }
}
